import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import {
  getLandingPageTitleColor,
  getThemeButtonGradient,
  getThemeButtonTextColor,
} from "../../functions/themeAssets";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  ensureStageRowId,
  fetchGameStageRecord,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markStageCompleted,
  saveGameStageAndReport,
  saveSnakesLaddersAnswerProgress,
  setStageActive,
} from "../../functions/stageReportSync";
import {
  MAX_GRID_OPTIONS,
  generateLayout,
  generateLayoutOptions,
  getCellDirection,
  getWordStartPositions,
  hashSeed,
} from "../../functions/crosswordLayout";
import { getCrosswordPairs } from "./crosswordUtils";
import { questionMediaUrl, toLayoutWords } from "../../functions/crosswordQuestions";
import { isThemeFlagOn } from "../gameSetupStorage";
import "./dragdrop.css";
import "./crossword.css";

const STAGE_NUM = 1;
const DEMO_PLANS_URL = "https://extramileplay.com/plans";
const MIN_PAIRS = 2;
/** Used when the theme predates the admin hint settings (hint_enabled / hint_penalty). */
const DEFAULT_HINT_PENALTY = 2;
const DEFAULT_HINT_TIME_PENALTY = 10;
const HINT_PENALTY_TYPES = ["none", "points", "time", "both"];

/** Admin hint settings. Themes saved before hint_penalty_type existed were points-only. */
function getHintSettings(themeData) {
  const rawType = String(themeData?.hint_penalty_type ?? "").trim().toLowerCase();
  const type = HINT_PENALTY_TYPES.includes(rawType) ? rawType : "points";
  const points = parseInt(themeData?.hint_penalty, 10);
  const seconds = parseInt(themeData?.hint_time_penalty, 10);
  return {
    points:
      type === "points" || type === "both"
        ? Number.isFinite(points) && points >= 0 ? points : DEFAULT_HINT_PENALTY
        : 0,
    seconds:
      type === "time" || type === "both"
        ? Number.isFinite(seconds) && seconds >= 0 ? seconds : DEFAULT_HINT_TIME_PENALTY
        : 0,
  };
}

/** SweetAlert in the crossword's look (cw-alert-* in crossword.css). The popup is
 *  rendered outside .cw-page, so it gets the theme's button colors handed to it. */
function fireThemedAlert(themeData, { iconClass, customClass, ...options }) {
  const { start, end } = getThemeButtonGradient(themeData);
  const btnText = getThemeButtonTextColor(themeData);
  return Swal.fire({
    iconHtml: `<i class="fa-solid ${iconClass}" aria-hidden="true"></i>`,
    icon: "warning",
    buttonsStyling: false,
    reverseButtons: true,
    // The stage unlocks document scroll (see the dd-stage-open effect), so SweetAlert's
    // default body height reset + scrollbar padding would shift/jolt the page behind it.
    heightAuto: false,
    scrollbarPadding: false,
    returnFocus: false,
    customClass: {
      popup: "cw-alert-popup",
      icon: "cw-alert-icon",
      title: "cw-alert-title",
      htmlContainer: "cw-alert-html",
      confirmButton: "cw-alert-btn",
      cancelButton: "cw-alert-btn cw-alert-btn--ghost",
      ...customClass,
    },
    showClass: { popup: "cw-alert-popup--in" },
    didOpen: (popup) => {
      if (start) {
        popup.style.setProperty("--cw-start", start);
        popup.style.setProperty("--cw-end", end || start);
      }
      if (btnText) popup.style.setProperty("--cw-text", btnText);
    },
    ...options,
  });
}

/** "reveal one letter and deduct 5 points" / "…add 10 seconds to your time" / both. */
function describeHintPenalty({ points, seconds }) {
  const pts = `deduct ${points} point${points === 1 ? "" : "s"}`;
  const secs = `add ${seconds} second${seconds === 1 ? "" : "s"} to your time`;
  if (points > 0 && seconds > 0) return `reveal one letter, ${pts}, and ${secs}`;
  if (points > 0) return `reveal one letter and ${pts}`;
  return `reveal one letter and ${secs}`;
}

const TURN_ANIM_MS = 800;
/** Delay between neighbouring cells' flips when a word is solved (domino effect). */
const TURN_STAGGER_MS = 120;
const CONFETTI_COUNT = 90;
const CONFETTI_COLORS = ["#ef3b5d", "#f5b82e", "#22c55e", "#2f6fed", "#9333ea", "#14b8a6", "#ffffff"];

/** Random falling pieces for the "all words solved" celebration (styled in crossword.css). */
function buildConfettiPieces(themeColor) {
  const colors = themeColor ? [themeColor, ...CONFETTI_COLORS] : CONFETTI_COLORS;
  return Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 1.2,
    duration: 2.6 + Math.random() * 1.8,
    drift: (Math.random() - 0.5) * 160,
    spin: 360 + Math.random() * 720,
    width: 7 + Math.random() * 7,
    round: Math.random() < 0.25,
    color: colors[i % colors.length],
  }));
}

/* Grid cell sizing: cells shrink from MAX to fit the grid's rows in the window, but
   never below MIN (still tappable); GAP must match .cw-grid's grid-gap in crossword.css. */
const MAX_CELL_PX = 40;
const MIN_CELL_PX = 22;
const GRID_GAP_PX = 3;
/** Space kept free under the grid: .cw-grid-wrap's padding (12) + .dd-page's bottom
 *  padding (20) + a little air, so the page itself never needs to scroll. */
const GRID_BOTTOM_ROOM_PX = 40;
/** .cw-grid-wrap's vertical padding — the wrap (and so the clue panel beside it) is this
 *  much taller than the grid. */
const GRID_WRAP_PAD_PX = 12;

/* Fill colors for numbered word-start cells, cycled by clue number. */
const START_CELL_COLORS = [
  "#ef3b5d",
  "#9333ea",
  "#2f6fed",
  "#f59e0b",
  "#14b8a6",
  "#ec4899",
  "#22c55e",
  "#8b5cf6",
];

/** 0-indexed {row, col} cells a layout word occupies. */
function wordCells(word) {
  const r0 = word.starty - 1;
  const c0 = word.startx - 1;
  const cells = [];
  for (let k = 0; k < word.answer.length; k++) {
    cells.push(
      word.orientation === "across" ? { row: r0, col: c0 + k } : { row: r0 + k, col: c0 }
    );
  }
  return cells;
}

function findWordAt(layout, row, col, orientation) {
  if (!layout) return null;
  return (
    layout.result.find(
      (w) => w.orientation === orientation && wordCells(w).some((c) => c.row === row && c.col === col)
    ) || null
  );
}

/** True when `word` actually begins (not just passes through) this cell. */
function wordStartsAt(word, row, col) {
  return !!word && word.starty - 1 === row && word.startx - 1 === col;
}

/**
 * Which direction a click on (row, col) should activate. A cell that starts a
 * fresh across word (but isn't also a down-word start) should always select
 * across, and vice versa — regardless of whichever direction was active
 * before. Only when the cell starts both, or starts neither (mid-word
 * crossing), do we fall back to the sticky direction the player was already
 * typing in.
 */
function naturalDirectionAt(layout, row, col, sticky) {
  const acrossWord = findWordAt(layout, row, col, "across");
  const downWord = findWordAt(layout, row, col, "down");
  const acrossStarts = wordStartsAt(acrossWord, row, col);
  const downStarts = wordStartsAt(downWord, row, col);
  if (acrossStarts && !downStarts) return "across";
  if (downStarts && !acrossStarts) return "down";
  if (sticky && findWordAt(layout, row, col, sticky)) return sticky;
  if (acrossWord) return "across";
  if (downWord) return "down";
  return sticky || "across";
}

function getStoredUser(authUser) {
  if (authUser && typeof authUser === "object") return authUser;
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
  } catch {
    return {};
  }
}

const GRID_SEED_KEY = "cwGridSeed";

/** Picks which of the admin's grid options a player gets: from the player's id, so they
 *  keep the same grid on refresh; players without an id get a random pick for the session. */
function getGridSeed(storedUser) {
  const userId = storedUser?.userId ?? storedUser?.userid ?? storedUser?.id;
  if (userId != null && String(userId).trim() !== "") return hashSeed(String(userId).trim());
  try {
    const saved = parseInt(sessionStorage.getItem(GRID_SEED_KEY), 10);
    if (Number.isFinite(saved)) return saved;
    const fresh = Math.floor(Math.random() * 4294967296);
    sessionStorage.setItem(GRID_SEED_KEY, String(fresh));
    return fresh;
  } catch {
    return Math.floor(Math.random() * 4294967296);
  }
}

function formatClock(totalSec) {
  const sec = Math.max(0, Math.floor(Number(totalSec) || 0));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function toSentenceCase(text) {
  const s = String(text ?? "");
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;
}

function buildInitialGrid(table) {
  return table.map((row) =>
    row.map((val) => ({
      block: val === "-",
      correctLetter: val === "-" ? null : val.toUpperCase(),
      value: "",
      status: "empty",
    }))
  );
}

export default function Game() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.auth);
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);
  const adminToken = useSelector(selectAdminToken);

  const storedUser = useMemo(() => getStoredUser(user), [user]);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser.role || "").toLowerCase();
    const source = String(storedUser.source || "").toUpperCase();
    const sessionId = String(storedUser.sessionId || "");
    return (
      sessionId === "demobypass" ||
      role === "demobypass" ||
      source === "DEMO" ||
      sessionId.startsWith("demo")
    );
  }, [storedUser]);

  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

  const pairs = useMemo(() => getCrosswordPairs(themeData), [themeData]);
  const pointsPerCorrect = Math.max(1, parseInt(themeData?.points, 10) || 1);
  const hintEnabled = isThemeFlagOn(themeData?.hint_enabled, true);
  const hintSettings = useMemo(() => getHintSettings(themeData), [themeData]);
  const titleColor = getLandingPageTitleColor(themeData) || "#000000";
  const heading = String(themeData?.landing_page_title || "").trim();

  // Admin "Shuffle Grid": on → each player gets one of the grid options shown on the
  // admin Grid Pattern page; off → everyone gets the standard grid.
  const gridShuffle = isThemeFlagOn(themeData?.grid_shuffle, false);
  const gridSeed = useMemo(() => getGridSeed(storedUser), [storedUser]);

  const layout = useMemo(() => {
    if (pairs.length < MIN_PAIRS) return null;
    const words = toLayoutWords(pairs);
    if (!gridShuffle) return generateLayout(words);
    // Only build options up to the picked one; if fewer exist, wrap around them.
    const pick = gridSeed % MAX_GRID_OPTIONS;
    const options = generateLayoutOptions(words, pick + 1);
    return options[pick % options.length];
  }, [pairs, gridShuffle, gridSeed]);

  const directionGrid = useMemo(() => {
    if (!layout) return [];
    return layout.table.map((row, i) => row.map((_, j) => getCellDirection(i, j, layout.table)));
  }, [layout]);

  const wordStartPositions = useMemo(
    () => (layout ? getWordStartPositions(layout.result) : {}),
    [layout]
  );

  /** Words in clue-number order — hint #1 always starts here, like stage4's CROSSWORD_WORDS[0]. */
  const orderedWords = useMemo(() => {
    if (!layout) return [];
    return layout.result
      .filter((w) => w.orientation !== "none")
      .slice()
      .sort(
        (a, b) =>
          (wordStartPositions[`${a.starty},${a.startx}`] || 0) -
          (wordStartPositions[`${b.starty},${b.startx}`] || 0)
      );
  }, [layout, wordStartPositions]);

  const [grid, setGrid] = useState([]);
  const [completedWords, setCompletedWords] = useState(() => new Set());
  const [score, setScore] = useState(0);
  const [elapsedSec, setElapsedSec] = useState(0);
  /** Seconds added by time-penalty hints; counted in the shown and saved Time Taken. */
  const [hintPenaltySec, setHintPenaltySec] = useState(0);
  /** Set once every word is solved: { points, time, confetti } drives the celebration. */
  const [celebration, setCelebration] = useState(null);
  const [timerOn, setTimerOn] = useState(false);
  const [dbReady, setDbReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || ""
  );
  const [activeWord, setActiveWord] = useState(null);
  const [turningKeys, setTurningKeys] = useState(() => new Set());

  const scoreRef = useRef(0);
  /** Points from solved words; the score is this minus hint penalties and can go negative. */
  const earnedPointsRef = useRef(0);
  const hintPointPenaltyRef = useRef(0);
  const elapsedRef = useRef(0);
  const hintPenaltySecRef = useRef(0);
  const completedWordsRef = useRef(new Set());
  const answersRef = useRef([]);
  const finished = useRef(false);
  const startedRef = useRef(false);
  const dirRef = useRef(null);
  const activeWordRef = useRef(null);
  const isAutoNavRef = useRef(false);
  const hintsUsedRef = useRef(0);
  const hintConfirmOpenRef = useRef(false);
  const allSolvedRef = useRef(false);
  const firstHintUsedRef = useRef(false);
  const inputRefs = useRef(new Map());
  const gridWrapRef = useRef(null);
  /** Window height left below the grid's top edge — caps the cell size so no page scroll. */
  const [gridRoomPx, setGridRoomPx] = useState(null);

  useEffect(() => {
    dispatch(setBackButtonUrl("/rules"));
  }, [dispatch]);

  // Unlock document scroll for the stage (login/rules CSS locks body overflow on mobile portrait).
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const root = document.getElementById("root");
    body.classList.add("dd-stage-open");
    html.classList.add("dd-stage-open");

    const apply = () => {
      html.style.setProperty("overflow-x", "hidden", "important");
      html.style.setProperty("overflow-y", "auto", "important");
      html.style.setProperty("height", "auto", "important");
      html.style.setProperty("max-height", "none", "important");
      body.style.setProperty("overflow", "visible", "important");
      body.style.setProperty("height", "auto", "important");
      body.style.setProperty("max-height", "none", "important");
      if (root) {
        root.style.setProperty("overflow", "visible", "important");
        root.style.setProperty("height", "auto", "important");
      }
    };

    apply();
    window.addEventListener("resize", apply);
    window.addEventListener("orientationchange", apply);

    return () => {
      window.removeEventListener("resize", apply);
      window.removeEventListener("orientationchange", apply);
      body.classList.remove("dd-stage-open");
      html.classList.remove("dd-stage-open");
      ["overflow", "overflow-x", "overflow-y", "height", "max-height"].forEach((prop) => {
        html.style.removeProperty(prop);
        body.style.removeProperty(prop);
        root?.style.removeProperty(prop);
      });
    };
  }, []);

  // Measure how much window height is left for the grid (below the title/HUD) so the
  // cell size can be chosen to fit every row without scrolling the page.
  const hasGrid = grid.length > 0;
  useLayoutEffect(() => {
    if (!hasGrid) return undefined;
    const measure = () => {
      const wrap = gridWrapRef.current;
      if (!wrap) return;
      const top = wrap.getBoundingClientRect().top + window.scrollY;
      setGridRoomPx(Math.max(0, window.innerHeight - top - GRID_BOTTOM_ROOM_PX));
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, [hasGrid, dbReady, heading]);

  // Build the interactive grid once the layout is known.
  useEffect(() => {
    if (!layout) {
      setGrid([]);
      return;
    }
    setGrid(buildInitialGrid(layout.table));
  }, [layout]);

  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      setStageActive(STAGE_NUM, storedUser);
      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }
      try {
        const rowId = await ensureStageRowId({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          onStageRowId: (id) => {
            sessionStorage.setItem(STAGE_ROW_KEY, String(id));
            if (!cancelled) setStageRowId(String(id));
          },
        });
        const st = await fetchGameStageRecord({
          backendBase,
          adminToken,
          storedUser,
          stageId: rowId || sessionStorage.getItem(STAGE_ROW_KEY),
        });
        if (st && typeof st === "object") {
          hydrateCompletedStagesFromRecord(st);
          const redirect = getRedirectForPlayedStage(st, STAGE_NUM, {});
          if (redirect && redirect !== "/stage1" && redirect !== "/game") {
            if (!cancelled) navigate(redirect, { replace: true });
            return;
          }
        }
      } catch (err) {
        console.error("Game bootstrap:", err);
      } finally {
        if (!cancelled) setDbReady(true);
      }
    }
    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [adminToken, backendBase, isDemoBypass, navigate, storedUser]);

  // No crossword pairs configured — match old crosswordnew.php's implicit requirement.
  useEffect(() => {
    if (themeStatus === "loading" || !dbReady) return;
    if (pairs.length >= MIN_PAIRS) return;
    Swal.fire({
      icon: "warning",
      title: "No Questions Found",
      text: "Please add at least 2 question/answer pairs from Admin before continuing.",
      confirmButtonText: "OK",
    }).then(() => navigate("/rules", { replace: true }));
  }, [pairs.length, themeStatus, dbReady, navigate]);

  useEffect(() => {
    if (startedRef.current || !dbReady || !grid.length) return;
    startedRef.current = true;
    setTimerOn(true);
  }, [dbReady, grid.length]);

  useEffect(() => {
    if (!timerOn) return undefined;
    const id = setInterval(() => {
      elapsedRef.current += 1;
      setElapsedSec(elapsedRef.current);
    }, 1000);
    return () => clearInterval(id);
  }, [timerOn]);

  const saveProgressAfterAnswer = useCallback(
    async (pointsThisAnswer) => {
      if (isDemoBypass || !backendBase || !adminToken) return;
      const stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
      if (!stageId) return;
      try {
        await saveSnakesLaddersAnswerProgress({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          answerIndex: Math.max(1, Math.min(completedWordsRef.current.size, 13)),
          pointsThisAnswer,
          totalScore: scoreRef.current,
          time: formatClock(elapsedRef.current + hintPenaltySecRef.current),
          userAnswers: answersRef.current,
        });
      } catch (err) {
        console.error("saveProgressAfterAnswer:", err);
      }
    },
    [adminToken, backendBase, isDemoBypass, stageRowId, storedUser]
  );

  const persistAndLeave = useCallback(
    async (finalScore) => {
      const finalTime = formatClock(elapsedRef.current + hintPenaltySecRef.current);
      setTimerOn(false);
      try {
        if (!isDemoBypass && backendBase && adminToken) {
          const saveResult = await saveGameStageAndReport({
            backendBase,
            adminToken,
            storedUser,
            isDemoBypass,
            stageId: stageRowId || sessionStorage.getItem(STAGE_ROW_KEY),
            stageNumber: STAGE_NUM,
            score: finalScore,
            total_score: finalScore,
            time: finalTime,
            gameover: true,
            user_answers: answersRef.current,
          });
          if (saveResult?.stageId) {
            sessionStorage.setItem(STAGE_ROW_KEY, String(saveResult.stageId));
            setStageRowId(String(saveResult.stageId));
          }
        }
      } catch (err) {
        console.error("Game finish:", err);
      }
      clearStageActive(STAGE_NUM);
      markStageCompleted(STAGE_NUM, storedUser);
      if (isDemoBypass) {
        window.location.href = DEMO_PLANS_URL;
        return;
      }
      navigate("/thankyou", {
        replace: true,
        state: { points: finalScore, time: finalTime },
      });
    },
    [adminToken, backendBase, isDemoBypass, navigate, stageRowId, storedUser]
  );

  /** Moves focus one step within the active word, skipping cells already locked correct. */
  const focusSibling = useCallback(
    (row, col, step) => {
      if (!layout) return;
      const orientation = dirRef.current;
      // At a crossing cell, prefer the word actually being typed (activeWordRef)
      // over a fresh orientation lookup — the two only disagree when the cell is
      // shared between two words, which is exactly where a wrong findWordAt pick
      // would strand the cursor instead of advancing to the next cell.
      const active = activeWordRef.current;
      const activeContainsCell =
        active && wordCells(active).some((c) => c.row === row && c.col === col);
      const word = activeContainsCell
        ? active
        : findWordAt(layout, row, col, orientation) || active;
      if (!word) return;
      const cells = wordCells(word);
      const idx = cells.findIndex((c) => c.row === row && c.col === col);
      if (idx === -1) return;
      let next = idx + step;
      while (next >= 0 && next < cells.length) {
        const c = cells[next];
        if (grid[c.row]?.[c.col]?.status !== "correct") {
          const input = inputRefs.current.get(`${c.row}-${c.col}`);
          if (input) {
            isAutoNavRef.current = true;
            input.focus();
            return;
          }
        }
        next += step;
      }
    },
    [layout, grid]
  );

  const focusWord = useCallback(
    (word) => {
      if (!word) return;
      activeWordRef.current = word;
      setActiveWord(word);
      dirRef.current = word.orientation;
      const cells = wordCells(word);
      const target =
        cells.find((c) => grid[c.row]?.[c.col]?.status !== "correct") || cells[0];
      const input = inputRefs.current.get(`${target.row}-${target.col}`);
      if (input) {
        isAutoNavRef.current = true;
        input.focus();
      }
    },
    [grid]
  );

  /** Flip-to-green reveal for one cell, like index.html's .cell.turn. */
  const triggerCellTurn = useCallback((row, col, delay = 0) => {
    const cellKey = `${row}-${col}`;
    window.setTimeout(() => {
      setTurningKeys((prev) => new Set(prev).add(cellKey));
      window.setTimeout(() => {
        setTurningKeys((prev) => {
          const next = new Set(prev);
          next.delete(cellKey);
          return next;
        });
      }, TURN_ANIM_MS);
    }, delay);
  }, []);

  /** Domino flip across a completed word's cells, first letter to last. */
  const triggerTurnAnimation = useCallback(
    (word) => {
      wordCells(word).forEach((c, k) => triggerCellTurn(c.row, c.col, k * TURN_STAGGER_MS));
    },
    [triggerCellTurn]
  );

  const checkWordCompletion = useCallback(
    (nextGrid) => {
      if (!layout || finished.current) return;
      for (const word of layout.result) {
        if (word.orientation === "none") continue;
        if (completedWordsRef.current.has(word.answer)) continue;

        let allCorrect = true;
        for (let k = 0; k < word.answer.length; k++) {
          const r = word.orientation === "across" ? word.starty - 1 : word.starty - 1 + k;
          const c = word.orientation === "across" ? word.startx - 1 + k : word.startx - 1;
          if (nextGrid[r]?.[c]?.status !== "correct") {
            allCorrect = false;
            break;
          }
        }
        if (!allCorrect) continue;

        completedWordsRef.current.add(word.answer);
        setCompletedWords(new Set(completedWordsRef.current));
        earnedPointsRef.current += pointsPerCorrect;
        scoreRef.current = earnedPointsRef.current - hintPointPenaltyRef.current;
        setScore(scoreRef.current);
        triggerTurnAnimation(word);

        answersRef.current.push({
          questionText: word.clue,
          userAnswer: word.answer,
          correctAnswer: word.answer,
          isCorrect: true,
          questionNumber: completedWordsRef.current.size,
          answeredAt: new Date().toISOString(),
        });
        saveProgressAfterAnswer(pointsPerCorrect);

        // Demo sessions only get a taste of the first solved word, then a CTA to subscribe.
        if (isDemoBypass && completedWordsRef.current.size === 1) {
          finished.current = true;
          Swal.fire({
            icon: "info",
            text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
            confirmButtonText: "OK",
            allowOutsideClick: false,
            allowEscapeKey: false,
          }).then(() => {
            window.location.assign(DEMO_PLANS_URL);
          });
          return;
        }
      }

      // Every word solved: stop the clock and celebrate once the last word's flip ends.
      const totalWords = layout.result.filter((w) => w.orientation !== "none").length;
      if (!allSolvedRef.current && totalWords > 0 && completedWordsRef.current.size >= totalWords) {
        allSolvedRef.current = true;
        setTimerOn(false);
        const points = scoreRef.current;
        const time = formatClock(elapsedRef.current + hintPenaltySecRef.current);
        // Wait for the longest word's domino flip to finish before celebrating.
        const longest = Math.max(...layout.result.map((w) => w.answer.length));
        window.setTimeout(() => {
          if (finished.current) return;
          setCelebration({
            points,
            time,
            confetti: buildConfettiPieces(getThemeButtonGradient(themeData).start),
          });
        }, TURN_ANIM_MS + (longest - 1) * TURN_STAGGER_MS);
      }
    },
    [
      layout,
      pointsPerCorrect,
      isDemoBypass,
      saveProgressAfterAnswer,
      triggerTurnAnimation,
      themeData,
    ]
  );

  const handleCellInput = useCallback(
    (row, col, rawValue) => {
      if (finished.current || !grid.length) return;
      const cell = grid[row][col];
      if (cell.block) return;

      const value = rawValue.slice(-1).toUpperCase();
      if (!dirRef.current) {
        dirRef.current = directionGrid[row][col];
      }
      const isCorrect = value !== "" && value === cell.correctLetter;

      const next = grid.map((r) => r.slice());
      next[row][col] = {
        ...cell,
        value,
        status: value === "" ? "empty" : isCorrect ? "correct" : "incorrect",
      };
      setGrid(next);

      // Advance the cursor on any typed letter — right or wrong — so wrong
      // guesses don't strand the player on the same cell.
      if (value) {
        focusSibling(row, col, 1);
        if (isCorrect) checkWordCompletion(next);
      }
    },
    [grid, directionGrid, focusSibling, checkWordCompletion]
  );

  const handleCellKeyDown = useCallback(
    (row, col, e) => {
      if (e.key === "Tab") {
        e.preventDefault();
        const nextDir = dirRef.current === "across" ? "down" : "across";
        const word = findWordAt(layout, row, col, nextDir);
        if (word) focusWord(word);
        else dirRef.current = nextDir;
        return;
      }
      if (e.key.startsWith("Arrow")) {
        e.preventDefault();
        let nextRow = row;
        let nextCol = col;
        if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
          dirRef.current = "across";
          nextCol += e.key === "ArrowRight" ? 1 : -1;
        } else {
          dirRef.current = "down";
          nextRow += e.key === "ArrowDown" ? 1 : -1;
        }
        const word = findWordAt(layout, nextRow, nextCol, dirRef.current);
        if (word) {
          activeWordRef.current = word;
          setActiveWord(word);
        }
        const input = inputRefs.current.get(`${nextRow}-${nextCol}`);
        if (input) {
          isAutoNavRef.current = true;
          input.focus();
        }
        return;
      }
      if (e.key === "Backspace" && grid[row]?.[col]?.value === "") {
        if (!dirRef.current) dirRef.current = directionGrid[row][col];
        focusSibling(row, col, -1);
      }
    },
    [grid, directionGrid, focusSibling, layout, focusWord]
  );

  /** Typing in a clue's input box writes the letters into that word's grid cells. */
  const handleClueInput = useCallback(
    (word, rawValue) => {
      if (finished.current || !grid.length) return;
      const chars = rawValue.toUpperCase().slice(0, word.answer.length);
      const next = grid.map((r) => r.slice());
      wordCells(word).forEach((c, k) => {
        const cell = grid[c.row][c.col];
        if (cell.status === "correct") return;
        const ch = chars[k] && chars[k] !== " " ? chars[k] : "";
        next[c.row][c.col] = {
          ...cell,
          value: ch,
          status: ch === "" ? "empty" : ch === cell.correctLetter ? "correct" : "incorrect",
        };
      });
      setGrid(next);
      checkWordCompletion(next);
    },
    [grid, checkWordCompletion]
  );

  /**
   * Backspace/Delete in a clue's input box clears letters by grid position instead
   * of letting the browser shift the string — so wrong letters typed through the
   * cells can be erased from here too, and locked (correct) letters are skipped.
   */
  const handleClueKeyDown = useCallback(
    (word, e) => {
      if (e.key !== "Backspace" && e.key !== "Delete") return;
      if (finished.current || !grid.length) return;
      e.preventDefault();

      const cells = wordCells(word);
      const input = e.currentTarget;
      const selStart = input.selectionStart ?? cells.length;
      const selEnd = input.selectionEnd ?? selStart;
      const clearable = (k) => {
        const c = cells[k];
        const cell = c && grid[c.row]?.[c.col];
        return !!cell && cell.status !== "correct" && cell.value !== "";
      };

      let targets = [];
      if (selEnd > selStart) {
        for (let k = selStart; k < Math.min(selEnd, cells.length); k++) {
          if (clearable(k)) targets.push(k);
        }
      } else if (e.key === "Backspace") {
        for (let k = Math.min(selStart, cells.length) - 1; k >= 0; k--) {
          if (clearable(k)) {
            targets = [k];
            break;
          }
        }
      } else {
        for (let k = selStart; k < cells.length; k++) {
          if (clearable(k)) {
            targets = [k];
            break;
          }
        }
      }
      if (!targets.length) return;

      const next = grid.map((r) => r.slice());
      targets.forEach((k) => {
        const c = cells[k];
        next[c.row][c.col] = { ...grid[c.row][c.col], value: "", status: "empty" };
      });
      setGrid(next);

      // Keep the caret where the deletion happened once React re-renders the value.
      const caret = e.key === "Backspace" || selEnd > selStart ? targets[0] : selStart;
      window.requestAnimationFrame(() => {
        try {
          input.setSelectionRange(caret, caret);
        } catch {
          /* input unmounted */
        }
      });
    },
    [grid]
  );

  const revealHintLetter = useCallback(() => {
    if (finished.current || !grid.length || !layout) return;

    let word;
    if (!firstHintUsedRef.current) {
      firstHintUsedRef.current = true;
      word = orderedWords.find((w) => !completedWordsRef.current.has(w.answer)) || orderedWords[0];
    } else {
      word = activeWordRef.current;
      if (!word || word.orientation === "none" || completedWordsRef.current.has(word.answer)) {
        word = layout.result.find(
          (w) => w.orientation !== "none" && !completedWordsRef.current.has(w.answer)
        );
      }
    }
    if (!word) return;

    // Always keep the highlighted word (cw-cell--active) in sync with whichever
    // word the hint actually reveals a letter in — same as stage4's applyHintReveal.
    activeWordRef.current = word;
    setActiveWord(word);
    dirRef.current = word.orientation;
    const target = wordCells(word).find(
      (c) => grid[c.row]?.[c.col]?.status !== "correct"
    );
    if (!target) return;

    const cell = grid[target.row][target.col];
    const next = grid.map((r) => r.slice());
    next[target.row][target.col] = {
      ...cell,
      value: cell.correctLetter,
      status: "correct",
    };
    setGrid(next);

    hintsUsedRef.current += 1;
    hintPointPenaltyRef.current += hintSettings.points;
    scoreRef.current = earnedPointsRef.current - hintPointPenaltyRef.current;
    setScore(scoreRef.current);
    if (hintSettings.seconds > 0) {
      hintPenaltySecRef.current += hintSettings.seconds;
      setHintPenaltySec(hintPenaltySecRef.current);
    }

    const input = inputRefs.current.get(`${target.row}-${target.col}`);
    if (input) {
      isAutoNavRef.current = true;
      input.focus();
    }

    checkWordCompletion(next);
  }, [grid, layout, orderedWords, checkWordCompletion, hintSettings]);

  const applyHint = useCallback(async () => {
    if (finished.current || hintConfirmOpenRef.current) return;
    // No penalty → reveal straight away; otherwise the player confirms first.
    if (hintSettings.points > 0 || hintSettings.seconds > 0) {
      hintConfirmOpenRef.current = true;
      const { isConfirmed } = await fireThemedAlert(themeData, {
        iconClass: "fa-lightbulb",
        title: "Use a hint?",
        text: `Using this hint will ${describeHintPenalty(hintSettings)}. Continue?`,
        showCancelButton: true,
        confirmButtonText: "Use Hint",
        cancelButtonText: "Cancel",
      });
      hintConfirmOpenRef.current = false;
      if (!isConfirmed) return;
    }
    revealHintLetter();
  }, [hintSettings, themeData, revealHintLetter]);

  const handleSubmit = useCallback(async () => {
    if (finished.current || submitting) return;
    // Every started word must be filled to the last cell, and at least one word must be
    // complete — a few loose letters don't count as an answer.
    const words = (layout?.result || []).filter((w) => w.orientation !== "none");
    const isFilled = (c) => !!grid[c.row]?.[c.col]?.value?.trim();
    const fullWords = words.filter((w) => wordCells(w).every(isFilled));
    // Letters shared with a completed crossing word don't make this word "started".
    const fullCells = new Set(
      fullWords.flatMap((w) => wordCells(w).map((c) => `${c.row}-${c.col}`))
    );
    const partialWords = words.filter(
      (w) =>
        !fullWords.includes(w) &&
        wordCells(w).some((c) => isFilled(c) && !fullCells.has(`${c.row}-${c.col}`))
    );
    const hasFullWord = fullWords.length > 0;

    if (partialWords.length) {
      const nums = partialWords
        .map((w) => ({ w, num: wordStartPositions[`${w.starty},${w.startx}`] }))
        .sort((a, b) => a.num - b.num);
      const list = nums.map(({ w, num }) => `${num} ${w.orientation}`).join(", ");
      fireThemedAlert(themeData, {
        iconClass: "fa-pencil",
        title: "Finish your words",
        html:
          '<p class="cw-alert-text">Fill every letter of a word before you submit. ' +
          `Incomplete: ${list}.</p>`,
        confirmButtonText: "Got it",
      }).then(() => focusWord(nums[0].w));
      return;
    }
    if (!hasFullWord) {
      fireThemedAlert(themeData, {
        iconClass: "fa-pencil",
        title: "No answers yet",
        html:
          '<div class="cw-alert-tiles" aria-hidden="true">' +
          ["E", "M", "P", "T", "Y"].map((ch) => `<span>${ch}</span>`).join("") +
          "</div>" +
          '<p class="cw-alert-text">Fill in at least one complete word on the grid before you submit.</p>',
        confirmButtonText: "Got it, let me play",
      });
      return;
    }
    finished.current = true;
    setSubmitting(true);
    await persistAndLeave(scoreRef.current);
  }, [submitting, persistAndLeave, grid, themeData, layout, wordStartPositions, focusWord]);

  const acrossWords = useMemo(
    () =>
      layout
        ? layout.result
            .filter((w) => w.orientation === "across")
            .sort(
              (a, b) =>
                wordStartPositions[`${a.starty},${a.startx}`] -
                wordStartPositions[`${b.starty},${b.startx}`]
            )
        : [],
    [layout, wordStartPositions]
  );
  const downWords = useMemo(
    () =>
      layout
        ? layout.result
            .filter((w) => w.orientation === "down")
            .sort(
              (a, b) =>
                wordStartPositions[`${a.starty},${a.startx}`] -
                wordStartPositions[`${b.starty},${b.startx}`]
            )
        : [],
    [layout, wordStartPositions]
  );

  const highlightedCells = useMemo(() => {
    if (!activeWord) return new Set();
    return new Set(wordCells(activeWord).map((c) => `${c.row}-${c.col}`));
  }, [activeWord]);

  const [clueTab, setClueTab] = useState("down");
  /** Image/video clue opened in the preview overlay:
   *  { type, src, num, heading, sentence } or null. */
  const [previewClue, setPreviewClue] = useState(null);

  useEffect(() => {
    if (!previewClue) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setPreviewClue(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [previewClue]);

  // Follow the player's direction: selecting a word in the grid shows its clue tab.
  useEffect(() => {
    if (activeWord?.orientation === "across" || activeWord?.orientation === "down") {
      setClueTab(activeWord.orientation);
    }
  }, [activeWord]);

  // Admin theme button colors drive the HUD pills, tabs and Submit (see crossword.css).
  const themeVars = useMemo(() => {
    const vars = { "--cw-border-color": titleColor };
    const { start, end } = getThemeButtonGradient(themeData);
    const text = getThemeButtonTextColor(themeData);
    if (start) {
      vars["--cw-start"] = start;
      vars["--cw-end"] = end || start;
    }
    if (text) vars["--cw-text"] = text;
    return vars;
  }, [themeData, titleColor]);

  const stillPreparing = pairs.length >= MIN_PAIRS && grid.length === 0 && !finished.current;

  if ((themeStatus === "loading" && !themeData) || !dbReady || stillPreparing) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  const renderClue = (w, dirKey) => {
    const num = wordStartPositions[`${w.starty},${w.startx}`];
    const done = completedWords.has(w.answer);
    const active =
      activeWord?.orientation === w.orientation &&
      activeWord?.starty === w.starty &&
      activeWord?.startx === w.startx;
    const inputValue = wordCells(w)
      .map((c) => grid[c.row]?.[c.col]?.value || " ")
      .join("")
      .trimEnd();
    const mediaSrc = w.type === "image" || w.type === "video" ? questionMediaUrl(w.media) : "";
    return (
      <li
        key={`${dirKey}-${num}`}
        className={`cw-clue${active ? " cw-clue--active" : ""}${done ? " cw-clue--done" : ""}`}
        onClick={() => !done && focusWord(w)}
      >
        <span className="cw-clue-num">{num}.</span>
        <span className="cw-clue-body">
          {mediaSrc ? (
            <>
              {w.heading ? <span className="cw-clue-heading">{w.heading}</span> : null}
              {/* The media + its sentence open in a preview; this click shouldn't
                  also move focus into the grid. */}
              <button
                type="button"
                className="cw-clue-view-link"
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewClue({
                    type: w.type,
                    src: mediaSrc,
                    num,
                    heading: w.heading,
                    sentence: w.sentence,
                  });
                }}
              >
                <i
                  className={`fa-solid ${w.type === "image" ? "fa-image" : "fa-circle-play"}`}
                  aria-hidden="true"
                />{" "}
                View {w.type}
              </button>
            </>
          ) : (
            <span className="cw-clue-text">{w.clue}</span>
          )}
          {done ? (
            <span className="cw-clue-answer">{toSentenceCase(w.answer)}</span>
          ) : active ? (
            <input
              type="text"
              className="cw-clue-input"
              // Sized per letter in em (not ch): bold, spaced capitals like W/M are far
              // wider than "0", so a ch width cropped the end of the word.
              style={{ width: `calc(${Math.max(w.answer.length, 4)} * 1.15em + 0.5em)` }}
              value={inputValue}
              maxLength={w.answer.length}
              autoComplete="off"
              placeholder={"_".repeat(w.answer.length)}
              onClick={(e) => e.stopPropagation()}
              onFocus={() => {
                activeWordRef.current = w;
                setActiveWord(w);
                dirRef.current = w.orientation;
              }}
              onKeyDown={(e) => handleClueKeyDown(w, e)}
              onChange={(e) => handleClueInput(w, e.target.value)}
            />
          ) : null}
        </span>
      </li>
    );
  };

  const pointsLabel = String(Math.floor(score));
  const hintPenaltyLabel = [
    hintSettings.points > 0 ? `−${hintSettings.points} pts` : "",
    hintSettings.seconds > 0 ? `+${hintSettings.seconds}s` : "",
  ]
    .filter(Boolean)
    .join(", ");
  const visibleClues = clueTab === "down" ? downWords : acrossWords;

  // Largest cell (up to MAX_CELL_PX) that lets every row fit in the measured height;
  // the grid's width then follows from the cell size (and still shrinks on narrow screens).
  let cellPx = MAX_CELL_PX;
  if (layout && gridRoomPx != null) {
    const fit = Math.floor((gridRoomPx - GRID_GAP_PX * (layout.rows - 1)) / layout.rows);
    cellPx = Math.max(MIN_CELL_PX, Math.min(MAX_CELL_PX, fit));
  }
  const gridMaxWidthPx = layout ? cellPx * layout.cols + GRID_GAP_PX * (layout.cols - 1) : 0;

  return (
    // No background here: the theme background is set on <body> (see dddUI/user.jsx).
    <div className="dd-page cw-page" style={themeVars}>
      <div className="cw-topbar">
        {heading ? (
          <h3 className="cw-title" style={{ color: titleColor }}>
            {heading}
          </h3>
        ) : null}

        <div className="cw-hud">
          <div className="cw-hud-pill" aria-label={`Time ${formatClock(elapsedSec + hintPenaltySec)}`}>
            <span className="cw-hud-icon" aria-hidden>
              <svg viewBox="0 0 48 48" width="34" height="34">
                <path d="M9 18a8 8 0 0 1 11-11" fill="#e53935" stroke="#9f1d1d" strokeWidth="1.5" />
                <path d="M39 18a8 8 0 0 0-11-11" fill="#e53935" stroke="#9f1d1d" strokeWidth="1.5" />
                <path d="M13 40l-3 4M35 40l3 4" stroke="#9f1d1d" strokeWidth="3" strokeLinecap="round" />
                <circle cx="24" cy="26" r="16" fill="#e53935" stroke="#9f1d1d" strokeWidth="1.5" />
                <circle cx="24" cy="26" r="12" fill="#fff" />
                <path
                  d="M24 18v8l5 4"
                  fill="none"
                  stroke="#1f2937"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="cw-hud-value">Time: {formatClock(elapsedSec + hintPenaltySec)}</span>
          </div>
          <div className="cw-hud-pill" aria-label={`Points ${pointsLabel}`}>
            <span className="cw-hud-icon" aria-hidden>
              <svg viewBox="0 0 48 48" width="34" height="34">
                <path
                  d="M24 4.5l5.4 11 12.1 1.8-8.8 8.5 2.1 12.1L24 32.4 13.2 37.9l2.1-12.1-8.8-8.5 12.1-1.8L24 4.5z"
                  fill="#c47810"
                  transform="translate(0 2.5)"
                />
                <path
                  d="M24 4.5l5.4 11 12.1 1.8-8.8 8.5 2.1 12.1L24 32.4 13.2 37.9l2.1-12.1-8.8-8.5 12.1-1.8L24 4.5z"
                  fill="url(#ddStarFill)"
                  stroke="#e8a010"
                  strokeWidth="0.8"
                />
                <defs>
                  <linearGradient id="ddStarFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#fff3a0" />
                    <stop offset="45%" stopColor="#ffd54a" />
                    <stop offset="100%" stopColor="#f0a020" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
            <span className="cw-hud-value">Points: {pointsLabel}</span>
          </div>
        </div>
      </div>

      <div
        className="cw-layout"
        style={
          gridRoomPx != null
            ? { "--cw-room": `${gridRoomPx + GRID_WRAP_PAD_PX}px` }
            : undefined
        }
      >
        <div className="cw-left">
          <div className="cw-grid-wrap" ref={gridWrapRef}>
            {layout ? (
              <div
                className="cw-grid"
                style={{
                  gridTemplateColumns: `repeat(${layout.cols}, minmax(0, 1fr))`,
                  gridTemplateRows: `repeat(${layout.rows}, minmax(0, 1fr))`,
                  aspectRatio: `${layout.cols} / ${layout.rows}`,
                  maxWidth: `${gridMaxWidthPx}px`,
                  "--cw-cell": `${cellPx}px`,
                }}
              >
                {grid.map((row, i) =>
                  row.map((cell, j) => {
                    const posKey = `${i + 1},${j + 1}`;
                    const number = wordStartPositions[posKey];
                    if (cell.block) {
                      return <div key={`${i}-${j}`} className="cw-cell cw-cell--block" />;
                    }
                    const cellKey = `${i}-${j}`;
                    const isHighlighted = highlightedCells.has(cellKey);
                    const isTurning = turningKeys.has(cellKey);
                    return (
                      <div
                        key={cellKey}
                        className={`cw-cell${number ? " cw-cell--start" : ""}${
                          cell.status === "correct" ? " cw-cell--correct" : ""
                        }${cell.status === "incorrect" ? " cw-cell--incorrect" : ""}${
                          isHighlighted ? " cw-cell--active" : ""
                        }${isTurning ? " cw-cell--turn" : ""}`}
                        style={
                          number
                            ? {
                                "--start-color":
                                  START_CELL_COLORS[(number - 1) % START_CELL_COLORS.length],
                              }
                            : undefined
                        }
                      >
                        {number ? <span className="cw-cell-number">{number}</span> : null}
                        <input
                          ref={(el) => {
                            if (el) inputRefs.current.set(`${i}-${j}`, el);
                            else inputRefs.current.delete(`${i}-${j}`);
                          }}
                          className="cw-cell-input"
                          maxLength={1}
                          value={cell.value}
                          disabled={cell.status === "correct"}
                          onFocus={(e) => {
                            // A cell already holding a character (typically one filled
                            // by the crossing word) is at maxLength — without selecting
                            // it first, the browser silently swallows the next keystroke
                            // instead of overwriting it.
                            e.target.select();
                            if (isAutoNavRef.current) {
                              isAutoNavRef.current = false;
                              return;
                            }
                            const preferDir = naturalDirectionAt(layout, i, j, dirRef.current);
                            const word =
                              findWordAt(layout, i, j, preferDir) ||
                              findWordAt(layout, i, j, preferDir === "across" ? "down" : "across");
                            if (word) {
                              activeWordRef.current = word;
                              setActiveWord(word);
                              dirRef.current = word.orientation;
                            }
                          }}
                          onChange={(e) => handleCellInput(i, j, e.target.value)}
                          onKeyDown={(e) => handleCellKeyDown(i, j, e)}
                        />
                      </div>
                    );
                  })
                )}
              </div>
            ) : null}
          </div>
        </div>

        <aside className="cw-panel">
          <div className="cw-tabs" role="tablist">
            {["down", "across"].map((dir) => (
              <button
                key={dir}
                type="button"
                role="tab"
                aria-selected={clueTab === dir}
                className={`cw-tab${clueTab === dir ? " cw-tab--active" : ""}`}
                onClick={() => setClueTab(dir)}
              >
                {dir === "across" ? "Across" : "Down"}
              </button>
            ))}
          </div>

          <ol className="cw-clue-list">
            {visibleClues.length ? (
              visibleClues.map((w) => renderClue(w, clueTab))
            ) : (
              <li className="cw-clue cw-clue--empty">No {clueTab} clues</li>
            )}
          </ol>

          <div className="cw-panel-actions">
            <button
              type="button"
              className="cw-submit-btn"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? "Submitting…" : "Submit"}
            </button>
            {hintEnabled ? (
              <button
                type="button"
                className="cw-hint-btn"
                onClick={applyHint}
                disabled={submitting || finished.current}
              >
                <i className="fa-solid fa-lightbulb" aria-hidden="true" /> Hint
                {hintPenaltyLabel ? ` (${hintPenaltyLabel})` : ""}
              </button>
            ) : null}
          </div>
        </aside>
      </div>

      {previewClue ? (
        <div
          className="cw-image-preview"
          role="dialog"
          aria-modal="true"
          aria-label={`Clue ${previewClue.num} ${previewClue.type} preview`}
          onClick={() => setPreviewClue(null)}
        >
          <div className="cw-preview-card" onClick={(e) => e.stopPropagation()}>
            <span className="cw-preview-num">Clue {previewClue.num}</span>
            {previewClue.type === "image" ? (
              <img
                className="cw-preview-media"
                src={previewClue.src}
                alt={previewClue.heading || `Clue ${previewClue.num}`}
              />
            ) : (
              // Native controls give play / pause / volume; never autoplays.
              <video
                className="cw-preview-media"
                src={previewClue.src}
                controls
                controlsList="nodownload noplaybackrate"
                disablePictureInPicture
                playsInline
                preload="metadata"
              />
            )}
            {previewClue.heading ? (
              <h3 className="cw-preview-heading">{previewClue.heading}</h3>
            ) : null}
            {previewClue.sentence ? (
              <p className="cw-preview-sentence">{previewClue.sentence}</p>
            ) : null}
          </div>
          <button
            type="button"
            className="cw-image-preview-close"
            onClick={() => setPreviewClue(null)}
            aria-label="Close preview"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </div>
      ) : null}

      {celebration ? (
        <>
          {/* Pure-CSS confetti; plays once and ignores clicks. */}
          <div className="cw-confetti" aria-hidden="true">
            {celebration.confetti.map((c) => (
              <span
                key={c.id}
                className={`cw-confetti-piece${c.round ? " cw-confetti-piece--round" : ""}`}
                style={{
                  left: `${c.left}%`,
                  width: `${c.width}px`,
                  height: c.round ? `${c.width}px` : `${c.width * 1.6}px`,
                  background: c.color,
                  animationDelay: `${c.delay}s`,
                  animationDuration: `${c.duration}s`,
                  "--cw-drift": `${c.drift}px`,
                  "--cw-spin": `${c.spin}deg`,
                }}
              />
            ))}
          </div>

          {celebration.open !== false ? (
            <div className="cw-celebrate" role="dialog" aria-modal="true" aria-labelledby="cw-celebrate-title">
              <div className="cw-celebrate-card">
                <div className="cw-celebrate-badge" aria-hidden="true">
                  <i className="fa-solid fa-trophy" />
                </div>
                <h2 id="cw-celebrate-title" className="cw-celebrate-title">
                  Puzzle Solved!
                </h2>
                <p className="cw-celebrate-text">You filled in every word correctly. Brilliant work!</p>
                <div className="cw-celebrate-stats">
                  <div className="cw-celebrate-stat">
                    <span className="cw-celebrate-stat-label">Points</span>
                    <span className="cw-celebrate-stat-value">{celebration.points}</span>
                  </div>
                  <div className="cw-celebrate-stat">
                    <span className="cw-celebrate-stat-label">Time</span>
                    <span className="cw-celebrate-stat-value">{celebration.time}</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="cw-celebrate-btn"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? "Submitting…" : "Submit & See Results"}
                </button>
                <button
                  type="button"
                  className="cw-celebrate-link"
                  onClick={() => setCelebration((c) => (c ? { ...c, open: false } : c))}
                >
                  View my puzzle
                </button>
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      <div className="dd-rotate-overlay" aria-hidden>
        Please rotate your device for the best experience
      </div>
    </div>
  );
}
